/**
 * Lift2Fit — the shared warm-up session card.
 *
 * A warm-up is ONE short block of light movements that repeats before every
 * session in the week (0019). It is authored once and applied implicitly, so it
 * lives outside the day list as a distinctly-toned, explicitly-labelled card.
 *
 * This component is deliberately the same file for the lifter's own builder and
 * the coach's client builder: "coaches and lifters share the same structure for
 * the program". `editable` is false for a coach-owned block on the lifter's
 * side, which is the only difference between the two uses.
 *
 * The tint is never the only signal: the card always carries a `TimerIcon`, the
 * word "Warm-up" and the sentence explaining that it runs before every day.
 */
import { useState } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import { api, type ApiResult, type ProgrammeOverview, type ProgrammeRow, type ProgrammeWarmup } from '@/lib/api';
import { pluralize } from '@/lib/format';
import { formatPrescription, type PrescriptionLine } from '@/lib/prescription';
import { CheckIcon, PlusIcon, TimerIcon, TrashIcon } from '@/lib/icons';
import { Field, StatusChip } from './components';
import { MovementPicker } from './pickers';

export interface WarmupCardProps {
  warmup: ProgrammeWarmup | null;
  planId: string;
  /** The coach scope; absent means the lifter's own programme. */
  clientId?: string | null;
  /** False when the viewer may not change the block (coach-owned, read-only). */
  editable: boolean;
  unit: UnitSystem;
  busy: boolean;
  /** Every working prescription id, so a warm-up reorder can name the full set. */
  otherPlanExerciseIds: readonly string[];
  /** Apply a refreshed overview (and print the API's own failure reason). */
  onApplied: (result: ApiResult<ProgrammeOverview>, message: string) => void;
  /** Copy only: the patient on the card (`your` / `their`). */
  audience?: 'lifter' | 'coach';
  testId?: string;
}

/** The warm-up movement printed in the shared prescription vocabulary. */
function toLine(row: ProgrammeRow): PrescriptionLine {
  const distance = row.measure === 'distance_m' ? row.repMax || row.repMin : null;
  const duration = row.measure === 'time_s' ? row.repMax || row.repMin : null;
  return {
    supersetGroup: row.supersetGroup,
    prescribedSets: row.prescribedSets,
    repMin: row.measure === 'reps' || row.measure === 'amrap' ? row.repMin : null,
    repMax: row.measure === 'reps' || row.measure === 'amrap' ? row.repMax : null,
    measure: row.measure,
    perSide: row.perSide,
    isBodyweight: row.isBodyweight,
    tempo: row.tempo,
    targetRpe: row.targetRpe,
    percent1rm: row.percent1rm,
    targetWeightKg: row.targetWeightKg,
    targetDistanceM: distance,
    targetDurationS: duration,
    restSeconds: row.restSeconds,
  };
}

interface WarmupDraft {
  measure: 'reps' | 'time_s';
  reps: string;
  durationS: string;
  restSeconds: string;
  notes: string;
}

function draftFromRow(row: ProgrammeRow): WarmupDraft {
  return {
    measure: row.measure === 'time_s' ? 'time_s' : 'reps',
    reps: String(row.repMax || row.repMin || 10),
    durationS: String(row.targetDurationS ?? row.repMax ?? 30),
    restSeconds: String(row.restSeconds),
    notes: row.notes ?? '',
  };
}

function clampInt(value: string, min: number, max: number, fallback: number): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

export function WarmupCard(props: WarmupCardProps) {
  const {
    warmup,
    planId,
    clientId = null,
    editable,
    unit,
    busy,
    otherPlanExerciseIds,
    onApplied,
    audience = 'lifter',
    testId = 'warmup-card',
  } = props;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<WarmupDraft | null>(null);

  const apply = (run: () => Promise<ApiResult<ProgrammeOverview>>, message: string) => {
    void run().then((result) => onApplied(result, message));
  };

  const addWarmup = () => {
    apply(
      () => api.createProgrammeWarmup(planId, { label: 'Warm-up' }, clientId),
      `Warm-up added — it runs before every day in this block.`,
    );
  };

  const removeWarmup = () => {
    apply(() => api.deleteProgrammeWarmup(planId, clientId), 'Warm-up removed; the days are unchanged.');
  };

  const addMovement = (exerciseId: string) => {
    if (!warmup) return;
    setPickerOpen(false);
    apply(
      () =>
        api.addProgrammeExercises(
          planId,
          warmup.dayId,
          {
            exerciseId,
            sets: 1,
            repMin: 10,
            repMax: 15,
            targetRpe: null,
            targetWeightKg: null,
            restSeconds: 30,
            measure: 'reps',
            notes: null,
          },
          clientId,
        ),
      'Movement added to the warm-up.',
    );
  };

  const removeMovement = (row: ProgrammeRow) => {
    apply(
      () => api.deleteProgrammeExercise(planId, row.planExerciseId, clientId),
      `${row.movement ?? row.exerciseName} removed from the warm-up.`,
    );
  };

  const moveMovement = (row: ProgrammeRow, direction: -1 | 1) => {
    if (!warmup) return;
    const ids = warmup.rows.map((entry) => entry.planExerciseId);
    const index = ids.indexOf(row.planExerciseId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    // The reorder endpoint names the plan's whole prescription set, so the
    // warm-up's new order is submitted with the working rows untouched.
    apply(
      () => api.reorderProgramme(planId, { planExerciseIds: [...next, ...otherPlanExerciseIds] }, clientId),
      'Warm-up order updated.',
    );
  };

  const saveMovement = (row: ProgrammeRow) => {
    if (!draft) return;
    const isTime = draft.measure === 'time_s';
    const durationS = clampInt(draft.durationS, 1, 3600, 30);
    const reps = clampInt(draft.reps, 1, 100, 10);
    const notes = draft.notes.trim();
    setEditingId(null);
    setDraft(null);
    apply(
      () =>
        api.patchProgrammeExercise(
          planId,
          row.planExerciseId,
          {
            sets: 1,
            repMin: isTime ? 1 : reps,
            repMax: isTime ? 1 : reps,
            restSeconds: clampInt(draft.restSeconds, 0, 900, 30),
            measure: draft.measure,
            targetRpe: null,
            targetWeightKg: null,
            notes: notes === '' ? null : notes.slice(0, 500),
            supersetGroup: null,
            groupType: null,
          },
          clientId,
        ),
      `${row.movement ?? row.exerciseName} updated.`,
    );
  };

  if (!warmup) {
    if (!editable) return null;
    return (
      <section
        data-testid={testId}
        data-warmup="none"
        aria-label="Warm-up"
        className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-dashed border-hairline bg-surface px-5 py-4"
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center border border-hairline text-ink-muted">
            <TimerIcon className="text-base" />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-bold uppercase tracking-[0.05em] text-ink">No warm-up on this block</span>
            <span className="text-[12px] text-ink-muted">
              Add one short block of light movements and it runs before every day — you only write it once.
            </span>
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          data-testid={`${testId}-add`}
          onClick={addWarmup}
          className="inline-flex h-10 items-center gap-2 border border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
        >
          <PlusIcon className="text-[12px]" /> Add a warm-up
        </button>
      </section>
    );
  }

  const totalMinutes = warmup.rows.reduce(
    (sum, row) => sum + Math.max(30, (row.targetDurationS ?? 0) + row.restSeconds),
    0,
  );

  return (
    <section
      data-testid={testId}
      data-warmup="present"
      aria-label={`${warmup.label} — shared by every day`}
      className="flex flex-col gap-3 rounded-card border border-hairline border-l-4 border-l-warning bg-warning/[0.06] px-5 py-4"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center bg-warning text-brand-charcoal">
            <TimerIcon className="text-base" />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-display text-[15px] font-black uppercase tracking-[-0.01em] text-ink">
                {warmup.label}
              </span>
              <StatusChip tone="warning" icon={<TimerIcon />}>
                Warm-up
              </StatusChip>
            </span>
            <span className="text-[12px] text-ink-muted" data-testid={`${testId}-explainer`}>
              Runs before every day of the block, {pluralize(warmup.rows.length, 'movement')}
              {warmup.rows.length ? ` · about ${Math.max(1, Math.round(totalMinutes / 60))} min` : ''}. It is shared, not
              repeated per day.
            </span>
          </div>
        </div>
        {editable ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              data-testid={`${testId}-add-movement`}
              onClick={() => setPickerOpen(true)}
              className="inline-flex h-9 items-center gap-1.5 border border-hairline bg-surface px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
            >
              <PlusIcon className="text-[11px]" /> Movement
            </button>
            <button
              type="button"
              disabled={busy}
              data-testid={`${testId}-remove`}
              onClick={removeWarmup}
              className="inline-flex h-9 items-center gap-1.5 border border-hairline bg-surface px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted hover:border-danger hover:text-danger disabled:opacity-40"
            >
              <TrashIcon className="text-[12px]" /> Remove warm-up
            </button>
          </div>
        ) : (
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted" data-testid={`${testId}-readonly`}>
            Read-only
          </span>
        )}
      </header>

      {warmup.rows.length === 0 ? (
        <p className="text-[12px] text-ink-muted" data-testid={`${testId}-empty`}>
          No movements yet. Add the first light movement — for example bodyweight squats for 30 seconds.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {warmup.rows.map((row, index) => (
            <li
              key={row.planExerciseId}
              data-testid={`${testId}-row-${index + 1}`}
              className="flex flex-col gap-2 border border-hairline bg-surface px-3 py-2.5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="l2f-tabular inline-flex h-6 w-6 shrink-0 items-center justify-center border border-hairline font-mono text-[11px] font-bold text-ink-muted">
                    {index + 1}
                  </span>
                  <span className="min-w-0 text-[13px] font-bold text-ink">
                    {row.movement ?? row.exerciseName}
                    {row.variant ? <span className="ml-1.5 font-normal text-ink-muted">{row.variant}</span> : null}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="l2f-tabular font-mono text-[12px] text-ink-muted">
                    {formatPrescription(toLine(row), unit)}
                  </span>
                  {editable ? (
                    <>
                      <button
                        type="button"
                        disabled={busy || index === 0}
                        aria-label={`Move ${row.movement ?? row.exerciseName} earlier`}
                        onClick={() => moveMovement(row, -1)}
                        className="inline-flex h-7 w-7 items-center justify-center border border-hairline text-[11px] text-ink-muted hover:border-accent hover:text-accent disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        disabled={busy || index === warmup.rows.length - 1}
                        aria-label={`Move ${row.movement ?? row.exerciseName} later`}
                        onClick={() => moveMovement(row, 1)}
                        className="inline-flex h-7 w-7 items-center justify-center border border-hairline text-[11px] text-ink-muted hover:border-accent hover:text-accent disabled:opacity-30"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        data-testid={`${testId}-edit-${index + 1}`}
                        onClick={() => {
                          setEditingId(row.planExerciseId);
                          setDraft(draftFromRow(row));
                                              }}
                        className="inline-flex h-7 items-center border border-hairline px-2 text-[10px] font-bold uppercase tracking-[0.06em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        aria-label={`Remove ${row.movement ?? row.exerciseName} from the warm-up`}
                        onClick={() => removeMovement(row)}
                        className="inline-flex h-7 w-7 items-center justify-center text-ink-muted hover:text-danger disabled:opacity-40"
                      >
                        <TrashIcon className="text-[12px]" />
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              {row.notes ? <p className="text-[11px] text-ink-muted">{row.notes}</p> : null}

              {editingId === row.planExerciseId && draft ? (
                <div className="grid grid-cols-2 gap-2 border-t border-hairline pt-2 sm:grid-cols-4">
                  <Field label="Measured in" testId={`${testId}-measure`}>
                    <select
                      aria-label="Measured in"
                      value={draft.measure}
                      data-testid={`${testId}-measure`}
                      onChange={(event) =>
                        setDraft({ ...draft, measure: event.target.value === 'time_s' ? 'time_s' : 'reps' })
                      }
                      className="h-10 w-full border border-hairline bg-page px-2 text-[13px] text-ink"
                    >
                      <option value="reps">Reps</option>
                      <option value="time_s">Seconds</option>
                    </select>
                  </Field>
                  {draft.measure === 'time_s' ? (
                    <Field label="Seconds" testId={`${testId}-seconds`}>
                      <input
                        type="number"
                        min={1}
                        max={3600}
                        aria-label="Seconds"
                        value={draft.durationS}
                        data-testid={`${testId}-seconds`}
                        onChange={(event) => setDraft({ ...draft, durationS: event.target.value })}
                        className="l2f-tabular h-10 w-full border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
                      />
                    </Field>
                  ) : (
                    <Field label="Reps" testId={`${testId}-reps`}>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        aria-label="Reps"
                        value={draft.reps}
                        data-testid={`${testId}-reps`}
                        onChange={(event) => setDraft({ ...draft, reps: event.target.value })}
                        className="l2f-tabular h-10 w-full border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
                      />
                    </Field>
                  )}
                  <Field label="Rest (s)" testId={`${testId}-rest`}>
                    <input
                      type="number"
                      min={0}
                      max={900}
                      step={5}
                      aria-label="Rest in seconds"
                      value={draft.restSeconds}
                      data-testid={`${testId}-rest`}
                      onChange={(event) => setDraft({ ...draft, restSeconds: event.target.value })}
                      className="l2f-tabular h-10 w-full border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
                    />
                  </Field>
                  <Field label="Note" testId={`${testId}-note`} className="col-span-2 sm:col-span-4">
                    <input
                      type="text"
                      maxLength={500}
                      aria-label="Note"
                      placeholder="Easy pace, no load"
                      value={draft.notes}
                      data-testid={`${testId}-note`}
                      onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                      className="h-10 w-full border border-hairline bg-page px-2 text-[13px] text-ink"
                    />
                  </Field>
                  <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-4">
                    <button
                      type="button"
                      disabled={busy}
                      data-testid={`${testId}-save`}
                      onClick={() => saveMovement(row)}
                      className="inline-flex h-10 items-center gap-1.5 bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
                    >
                      <CheckIcon className="text-[12px]" /> Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(null);
                        setDraft(null);
                      }}
                      className="inline-flex h-10 items-center border border-hairline px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editable ? (
        <p className="text-[11px] text-ink-muted">
          Keep it light: 10–15 minutes of easy movements with short rests. Warm-up sets never count towards volume, PRs or
          compliance.
        </p>
      ) : (
        <p className="text-[12px] text-ink-muted" data-testid={`${testId}-managed`}>
          {audience === 'lifter'
            ? 'Your coach set this warm-up. You can view it and log it, but only your coach can change it.'
            : 'This warm-up is managed by the coach.'}
        </p>
      )}

      {pickerOpen ? (
        <MovementPicker
          title="Add a movement to the warm-up"
          onClose={() => setPickerOpen(false)}
          onPick={(option) => addMovement(option.id)}
        />
      ) : null}
    </section>
  );
}
